/* eslint-disable no-unused-vars */
import React, { useContext, useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { ChoiceGroup, Dropdown, Link } from '@fluentui/react';
import { withPerfMarker } from '@bingads-webui/performance-marker-react-wrapper';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { getStepStyles } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { StepContainer } from '../step-container/step-container';
import { GoalCreationWizardContext } from '../../goal-creation-wizard-context';
import { WIZARD_ACTIONS } from '../../goal-creation-wizard-reducer';
import { TagSelectionDialog } from './tag-selection-dialog';
import { findTagById, getErrorMessage, processPayload, saveGoal, getExternalIntegrations } from '../../conversion-goals-repository';
import { endActivity } from '../../shared/util';
import { textWrapDropdownStyles } from '../../shared/overflow-dropdown-styles';

const TagSelectionSubstepUnstyled = (props) => {
  const {
    classes,
  } = props;
  const i18n = useContext(I18nContext);
  const wizardContext = useContext(GoalCreationWizardContext);
  const hasPreselectTag = !_.isUndefined(wizardContext.goalContext.Tag);
  const initialTagId = hasPreselectTag ? wizardContext.goalContext.Tag.Id : null;
  const isClarityGoal = !_.isEmpty(wizardContext.goalContext.ClarityEventDefinitionId);

  const [tagId, setTagId] = useState(initialTagId);
  const tagInstallOptionList = [
    { key: 'no', text: i18n.getString(_TL_('No, this UET tag is not installed on all your website pages')) },
    {
      key: 'yes',
      text: i18n.getString(_TL_('Yes, the UET tag was already installed on all website pages when you created another conversion goal or audience list')),
      onRenderField: (properties, render) => (
        <div className="tagConfirmation">
          {render(properties)}
          {properties.checked &&
            <div className="tagConfirmationText">
              <span>
                {i18n.getString(_TL_('For conversion tracking to work, make sure that the UET tag is on every page of your website and check that the tag ID you are using is'))} {tagId}
                {i18n.getString(_TL_('.'))}
              </span>
            </div>}
        </div>
      ),
    },
    { key: 'undetermined', text: i18n.getString(_TL_('I\'m not sure. I need instructions to install the tag.')) },
  ];

  const { logActivity } = wizardContext;
  const [isDialogHidden, setIsDialogHidden] = useState(true);
  const [isTagSelected, setIsTagSelected] = useState(hasPreselectTag);
  const tagNameDefault = hasPreselectTag ? findTagById(wizardContext.goalContext.tags, tagId).Name : null;
  const [tagName, setTagName] = useState(tagNameDefault);
  const [tagInstallOption, setTagInstallOption] = useState(wizardContext.tagInstallOption);
  const [notificationError, setNotificationError] = useState(null);
  const [footerEnabled, setFooterEnabled] = useState(true);
  const [isIntegratedGtm, setIsIntegratedGtm] = useState(false);
  const [shouldBlockerLoading, setShouldBlockerLoading] = useState(hasPreselectTag);
  const [gtmAccountId, setGtmAccountId] = useState(null);
  const [gtmContainerId, setGtmContainerId] = useState(null);

  const checkExternalIntegrations = useCallback((selectedTagId) => {
    setShouldBlockerLoading(true);
    getExternalIntegrations(wizardContext.cid, wizardContext.aid, selectedTagId)
      .then((response) => {
        if (response.hasExternalIntegrations && response.GtmIntegrations && response.GtmIntegrations.length > 0) {
          const gtmIntegration = response.GtmIntegrations[0]; // take first row for now
          setGtmAccountId(gtmIntegration.AccountId);
          setGtmContainerId(gtmIntegration.ContainerId);
          setIsIntegratedGtm(true);
        } else {
          setIsIntegratedGtm(false);
        }
        if (response.GtmIntegrations && response.GtmIntegrations.length > 1) {
          logActivity.trace(`[GtmIntegration] ${JSON.stringify(response.GtmIntegrations)}`); // log multiple external integration for better UI decision
        }
      })
      .finally(() => {
        setShouldBlockerLoading(false);
      });
  }, [logActivity, wizardContext.aid, wizardContext.cid]);

  useEffect(() => {
    if (shouldBlockerLoading) {
      checkExternalIntegrations(tagId);
    }
  }, [checkExternalIntegrations, shouldBlockerLoading, tagId]);

  const handleNextInternal = useCallback((selectedTagInstallOption) => {
    const defaultTag = wizardContext.goalContext.Tag || {};
    const hasAutoCreatedTag = !!defaultTag.isAutoCreated;
    const hasPreferredTag = !!defaultTag.IsPreferredTag;
    const selectedAutoCreatedTag = tagId === defaultTag.Id;
    const selectedPreferredTag = tagId === defaultTag.Id;

    const isEnhancedConversionsEnabled = wizardContext.goalContext.IsEnhancedConversionsEnabled;
    const EnhancedConversions = wizardContext.permissions && wizardContext.permissions.dynamic
      && wizardContext.permissions.dynamic.EnhancedConversions;

    logActivity.trace(JSON.stringify({
      hasPreselectTag,
      hasAutoCreatedTag,
      hasPreferredTag,
      selectedAutoCreatedTag,
      selectedPreferredTag,
      defaultTagId: defaultTag.Id || 0,
      selectedTagId: tagId,
      isEnhancedConversionsEnabled,
    }));

    const goToSetUpEnhancedConversions = isEnhancedConversionsEnabled && EnhancedConversions;
    const goalDelta = {
      Tag: {
        Id: tagId,
      },
    };
    const deleteClarityEvent = isClarityGoal && tagId !== initialTagId;
    if (deleteClarityEvent) {
      logActivity.trace(`Deleting clarity event because tagId changed: ${wizardContext.goalContext.ClarityEventDefinitionId}`);
    }
    const payload = processPayload({
      ...wizardContext.goalContext,
      ...goalDelta,
    }, wizardContext.permissions, deleteClarityEvent);
    saveGoal(payload, wizardContext)
      .then((response) => {
        endActivity(logActivity);
        wizardContext.dispatch({
          type: WIZARD_ACTIONS.NAVIGATE_WITH_TAGINSTALL_OPTIONS,
          tagInstallOption: selectedTagInstallOption,
          data: Object.assign({
            ...goalDelta,
            Id: response.Id,
          }, deleteClarityEvent ? {
            ClarityEventDefinitionId: null,
            ClarityEventDefinitionObject: null,
          } : {}),
          switchToEdit: true,
          goToSetUpEnhancedConversions,
        });
      })
      .catch((response) => {
        setFooterEnabled(true);
        setNotificationError(getErrorMessage(response, i18n));
      });
    setFooterEnabled(false);
  }, [wizardContext, tagId, logActivity, hasPreselectTag, isClarityGoal, initialTagId, i18n]);

  const handleNext = useCallback(() => {
    handleNextInternal(isIntegratedGtm ? 'yes' : tagInstallOption);
  }, [handleNextInternal, isIntegratedGtm, tagInstallOption]);

  const continueToTagInstructions = useCallback(() => {
    handleNextInternal('no');
  }, [handleNextInternal]);

  useEffect(() => {
    let notificationId;
    if (!_.isEmpty(notificationError)) {
      notificationId = notificationApi.showWarning(notificationError);
    }
    return () => notificationId && notificationApi.dismiss(notificationId);
  }, [notificationError]);

  const onChangeTagSelection = useCallback((event, option) => {
    logActivity.trace(`No preferred tag, picked selected tag:${option.key}`);
    setTagId(option.key);
    setIsTagSelected(true);
    checkExternalIntegrations(option.key);
  }, [checkExternalIntegrations, logActivity]);

  const onChangeTagIntallSelection = useCallback((event, option) => {
    logActivity.trace(`Changed tag install selection to ${option.key}`);
    setTagInstallOption(option.key);
  }, [logActivity]);

  const onShowDialog = useCallback(() => {
    logActivity.trace('Clicked link to change selected tag');
    setIsDialogHidden(false);
  }, [logActivity]);

  const onSaveDialog = useCallback((selectedTag) => {
    const preferredTag = wizardContext.goalContext.Tag && wizardContext.goalContext.Tag.Id;
    logActivity.trace(`Preferred tag exists: ${preferredTag}. Changed to ${selectedTag.id}`);
    setIsDialogHidden(true);
    setTagId(selectedTag.Id);
    setTagName(selectedTag.Name);
    checkExternalIntegrations(selectedTag.Id);
  }, [checkExternalIntegrations, logActivity, wizardContext.goalContext.Tag]);

  const onDismissDialog = useCallback(() => {
    logActivity.trace('Dismissed dialog to change selected tag');
    setIsDialogHidden(true);
  }, [logActivity]);

  const tagDropdownList = useMemo(() => {
    const options = [];
    const { cid } = wizardContext;
    const tagList = wizardContext.goalContext.tags;
    _.each(tagList, (tag) => {
      const tagText = cid === tag.Owner.Id ? tag.Name : `${tag.Name} - ${tag.Owner.Name}`;
      options.push({ key: tag.Id, text: tagText, ariaLabel: tagText });
    });

    const sortedOptions = options.sort((a, b) => a.text.localeCompare(b.text));
    return sortedOptions;
  }, [wizardContext]);

  const footerConfig = useMemo(() => ({
    buttons: [{
      type: 'Secondary',
      onClick: wizardContext.goBack,
      isEnabled: footerEnabled,
      text: i18n.getString(_TL_('Back')),
    },
    {
      type: 'Primary',
      onClick: handleNext,
      isEnabled: footerEnabled && isTagSelected,
      text: isIntegratedGtm ? i18n.getString(_TL_('Skip base tag setup')) : i18n.getString(_TL_('Save and next')),
    },
    {
      type: 'Cancel',
      onClick: wizardContext.cancel,
      isEnabled: footerEnabled,
      text: i18n.getString(_TL_('Cancel')),
    }],
  }), [wizardContext.goBack, wizardContext.cancel, footerEnabled, i18n, handleNext, isTagSelected, isIntegratedGtm]);

  return (
    <StepContainer
      title={i18n.getString(_TL_('Set up tagging'))}
      step="tag-selection"
      footerConfig={footerConfig}
    >
      <UiBlocker
        blocking={shouldBlockerLoading}
        loadingText={i18n.getString(_TL_('Loading...'))}
      >
        <div className={classes.contentBlock}>
          {hasPreselectTag ?
            (
              <div className="preferred-tag-selection">
                <p><span>{`${i18n.getString(_TL_('UET tag we are using:'))}`}</span><span style={{ fontWeight: 'bolder' }}>{` ${tagName}`}</span></p>
                <p>{`${i18n.getString(_TL_('Don’t see the tag you want?'))} `}
                  {// eslint-disable-next-line jsx-a11y/anchor-is-valid
                  }<Link id="open-tag-dialog" onClick={onShowDialog}>{i18n.getString(_TL_('Use a different tag'))}</Link>
                </p>
                {!isDialogHidden && <TagSelectionDialog
                  hiddenDefault={false}
                  onSaveDialog={onSaveDialog}
                  onDismissDialog={onDismissDialog}
                  tagOptions={tagDropdownList}
                  selectedTag={{ Id: tagId, Name: tagName }}
                  i18n={i18n}
                  isClarityGoal={isClarityGoal}
                />}
              </div>
            ) :
            (
              <div className="no-preferred-tag-selection">
                <Dropdown
                  label={i18n.getString(_TL_('Tag'))}
                  placeholder={i18n.getString(_TL_('Select a tag...'))}
                  selectedKey={tagId}
                  options={tagDropdownList}
                  onChange={onChangeTagSelection}
                  styles={{ dropdown: { width: 180 }, ...textWrapDropdownStyles }}
                />
              </div>
            )
          }
          <div className="instruction-text">
            {isTagSelected && !isIntegratedGtm && !shouldBlockerLoading &&
              <div className={classes.radioButtonsBlock}>
                <p id="uet-tag-install-choice-label" className={classes.bodyText}>{i18n.getString(_TL_('Do you have this UET tag installed on your website?'))}</p>
                <ChoiceGroup
                  selectedKey={tagInstallOption}
                  options={tagInstallOptionList}
                  onChange={onChangeTagIntallSelection}
                  aria-labelledby="uet-tag-install-choice-label"
                />
              </div>
            }
            {isIntegratedGtm && !shouldBlockerLoading &&
              <div className="external-integrations">
                <p>
                  <span>{i18n.getString(_TL_('Looks like you have already set up this UET tag with Google Tag Manager account '))}</span>
                  <span>{gtmAccountId}</span>
                  <span>{i18n.getString(_TL_(' and container '))}</span>
                  <span>{gtmContainerId}</span>
                  <ReactTemplate
                    template={`. ${i18n.getString(_TL_('Click {{a}}here{{/a}} to see the setup instructions again.'))}`}
                    model={{
                      // eslint-disable-next-line jsx-a11y/anchor-is-valid
                      a: text => <Link onClick={continueToTagInstructions} role="button" tabIndex={0}>{text}</Link>,
                    }}
                  />
                </p>
              </div>
            }
          </div>
        </div>
      </UiBlocker>
    </StepContainer>
  );
};

TagSelectionSubstepUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
};

export const TagSelectionSubstep = withPerfMarker(withDefaultStyles(TagSelectionSubstepUnstyled, getStepStyles));
